/**
 * M3X.WORKSPACE-CLOSURE Session 2 PR 9 — J/K/H/L keyboard navigation
 * for Rail 2.
 *
 * Document-level listener:
 *   J → next sub-domain (wraps at end)
 *   K → previous sub-domain (wraps at top)
 *   H → collapse focused sub-domain (if expanded)
 *   L → expand focused sub-domain (if collapsed)
 *   Enter → navigate to focused screen (when one is inside an
 *           expanded sub-domain), or expand the focused sub-domain
 *
 * Skips when:
 *   - target is <input>, <textarea>, [contenteditable]
 *   - modifier keys (Cmd, Ctrl, Alt) are pressed
 *   - chat panel is open AND focus inside it
 *
 * Focus ring rendered in Rail 2's SubDomainRow via the data-kbd-focus
 * attribute set on the focused element by this hook.
 */

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";


function _shouldSkip(e: KeyboardEvent): boolean {
  if (e.metaKey || e.ctrlKey || e.altKey) return true;
  const target = e.target as Element | null;
  if (!target) return false;
  const asHtml = target as HTMLElement;
  if (
    asHtml.tagName === "INPUT" ||
    asHtml.tagName === "TEXTAREA" ||
    asHtml.isContentEditable
  ) {
    return true;
  }
  // PR 9 spec — skip if focus is inside the chat panel. Document /
  // window dispatches arrive with non-Element targets; guard.
  if (typeof target.closest === "function" && target.closest("[data-chat-panel]")) {
    return true;
  }
  return false;
}


export function useRail2Keyboard(): void {
  const navigate = useNavigate();

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (_shouldSkip(e)) return;
      const key = e.key.toLowerCase();
      if (!["j", "k", "h", "l", "enter"].includes(key)) return;
      if (key === "enter" && e.key !== "Enter") return;

      // Resolve the visible sub-domain rows + screen links in DOM
      // order. Rail 2 exposes them via data-sub-domain + data-screen.
      const rail = document.querySelector('aside[aria-label="Sub-domains"]');
      if (!rail) return;
      const subDomains = Array.from(
        rail.querySelectorAll("[data-sub-domain]"),
      ) as HTMLElement[];
      if (subDomains.length === 0) return;

      const currentFocusedIdx = subDomains.findIndex(
        (sd) => sd.getAttribute("data-kbd-focus") === "true",
      );
      const idx = currentFocusedIdx === -1 ? 0 : currentFocusedIdx;

      function _setFocus(nextIdx: number) {
        subDomains.forEach((sd, i) => {
          if (i === nextIdx) {
            sd.setAttribute("data-kbd-focus", "true");
          } else {
            sd.removeAttribute("data-kbd-focus");
          }
        });
      }

      function _toggleExpand(target: HTMLElement, expand: boolean) {
        const btn = target.querySelector('button[aria-expanded]') as HTMLButtonElement | null;
        if (!btn) return;
        const isExpanded = btn.getAttribute("aria-expanded") === "true";
        if (expand && !isExpanded) btn.click();
        else if (!expand && isExpanded) btn.click();
      }

      if (key === "j") {
        e.preventDefault();
        const next = (idx + 1) % subDomains.length;
        _setFocus(next);
      } else if (key === "k") {
        e.preventDefault();
        const next = (idx - 1 + subDomains.length) % subDomains.length;
        _setFocus(next);
      } else if (key === "l") {
        e.preventDefault();
        _toggleExpand(subDomains[idx], true);
      } else if (key === "h") {
        e.preventDefault();
        _toggleExpand(subDomains[idx], false);
      } else if (e.key === "Enter") {
        e.preventDefault();
        const focused = subDomains[idx];
        // If a screen link is inside the focused sub-domain, navigate
        // to the FIRST one. Otherwise toggle expand.
        const firstScreen = focused.querySelector('[data-screen]') as HTMLAnchorElement | null;
        if (firstScreen) {
          const href = firstScreen.getAttribute("href");
          if (href) navigate(href);
        } else {
          _toggleExpand(focused, true);
        }
      }
    }

    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [navigate]);
}
